import React from "react";
import { Box } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import DietaryBadges from "../ItemCard/DietaryBadges";
import AltNames from "../../MenuName/AltNames";
import QtyStepper from "./QtyStepper";
import { servesLabel } from "../../../utils/serves";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, SHADOW, TYPE } from "../../../utils/designTokens";
import { resizedImage } from "../../../utils/resizedImage";

const lowestPrice = (food) => Math.min(...food.variations.map((v) => v.price));

/** "$12.00", or "From $9.00" when the item comes in sizes. */
export function priceLabel(food, currencySymbol) {
  const price = `${currencySymbol}${parseFloat(lowestPrice(food)).toFixed(2)}`;
  return food.variations.length > 1 ? `From ${price}` : price;
}

/** Red round "+" for adding the first one. */
export function AddCircleButton({ onClick, itemName, sx }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={`Add ${itemName}`}
      sx={{
        ...BUTTON_RESET,
        position: "relative",
        width: 40,
        height: 40,
        borderRadius: "50%",
        bgcolor: C.red,
        color: "#FFFFFF",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: SHADOW.button,
        transition: "background-color .15s ease, transform .15s ease",
        "&:hover": { bgcolor: C.redHover, transform: "scale(1.05)" },
        "&::before": { content: '""', position: "absolute", inset: -2 },
        ...FOCUS_RING,
        ...sx,
      }}
    >
      <AddRoundedIcon sx={{ fontSize: 22 }} />
    </Box>
  );
}

/** Small "AI image" / "AI generated" tag. */
export function AiTag({ children, onPhoto = false, sx }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: "6px",
        py: "1px",
        borderRadius: "6px",
        fontFamily: FONT_BODY,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        ...(onPhoto
          ? { bgcolor: "rgba(26,25,24,0.78)", color: "#FFFFFF" }
          : { bgcolor: C.surface2, color: C.muted }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** "#1 most liked" */
export function MostLikedBadge({ rank, sx }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        px: 1,
        borderRadius: RADIUS.pill,
        bgcolor: C.redSoft,
        color: C.redLink,
        fontFamily: FONT_BODY,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {`#${rank} most liked`}
    </Box>
  );
}

/** "92% (40)" with a thumbs up. */
export function LikedLine({ percent, votes }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: C.muted }}>
      <ThumbUpAltRoundedIcon aria-hidden="true" sx={{ fontSize: 14, color: C.green }} />
      <span>
        {`${percent}%`}
        {votes != null ? ` (${votes})` : ""}
      </span>
    </Box>
  );
}

/**
 * One dish in a category: words on the left, a 120px photo on the right with
 * the red + over its corner (a −/+ stepper once it is in the bag). A dish
 * without a photo is a text card with the + beside its price.
 *
 * The dish name is the card's button (stretched over the whole card), so the
 * + and stepper sit above it as their own buttons.
 */
function MenuItemCard({
  food,
  quantity = 0,
  onOpen,
  onAdd,
  onRemove,
  currencySymbol = "$",
  langDisplay = "NONE",
  cateringTarget = null,
  onOpenCatering,
}) {
  const soldOut = !!food.is86ed;
  const hasPhoto = !!food.image;
  const control = soldOut ? null : quantity > 0 ? (
    <QtyStepper quantity={quantity} onAdd={onAdd} onRemove={onRemove} itemName={food.title} floating={hasPhoto} />
  ) : (
    <AddCircleButton onClick={onAdd} itemName={food.title} />
  );

  return (
    <Box
      component="article"
      sx={{
        position: "relative",
        display: "flex",
        gap: 2,
        minHeight: hasPhoto ? 152 : 0,
        p: 2,
        boxSizing: "border-box",
        borderRadius: RADIUS.menuCard,
        border: `1px solid ${C.border}`,
        bgcolor: C.bg,
        opacity: soldOut ? 0.6 : 1,
        transition: "border-color .15s ease, box-shadow .15s ease",
        ...(soldOut
          ? {}
          : {
              "&:hover": { borderColor: C.borderStrong, boxShadow: "0 4px 14px rgba(26,25,24,0.06)" },
              "&:focus-within": { borderColor: C.borderStrong },
            }),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 0.75, rowGap: 0.5 }}>
          <Box
            component="button"
            type="button"
            onClick={onOpen}
            disabled={soldOut}
            aria-label={soldOut ? `${food.title}, unavailable` : undefined}
            sx={{
              ...BUTTON_RESET,
              ...TYPE.itemName,
              color: C.ink,
              textAlign: "left",
              textDecoration: soldOut ? "line-through" : "none",
              cursor: soldOut ? "not-allowed" : "pointer",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              // Stretched over the card: the whole card opens the dish.
              "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: RADIUS.menuCard, zIndex: 0 },
              "&:focus-visible": { outline: "none" },
              "&:focus-visible::after": { outline: `2px solid ${C.ink}`, outlineOffset: 2 },
            }}
          >
            {food.title}
            <AltNames alt1={food.altName1} alt2={food.altName2} displayMode={langDisplay} />
          </Box>
          <DietaryBadges tags={food.dietaryTags} />
          {food.mostLikedRank && food.mostLikedRank <= 3 && <MostLikedBadge rank={food.mostLikedRank} />}
        </Box>

        {food.likedPercent != null && (
          <Box sx={{ mt: 0.5 }}>
            <LikedLine percent={food.likedPercent} votes={food.voteCount} />
          </Box>
        )}
        {soldOut && (
          <Box sx={{ mt: 0.5, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.redLink }}>
            {food.stockLeft === 0 ? "Sold out at this stop" : "Temporarily unavailable"}
          </Box>
        )}
        {!soldOut && food.stockLeft != null && food.stockLeft <= 10 && (
          <Box sx={{ mt: 0.5, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: C.gold }}>
            {`Only ${food.stockLeft} left`}
          </Box>
        )}

        {food.description ? (
          <Box
            sx={{
              ...TYPE.body,
              color: C.muted,
              mt: 0.5,
              display: "-webkit-box",
              // One line instead of two makes room for the catering button.
              WebkitLineClamp: cateringTarget ? 1 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {food.description}
            {food.isAiDescription && <AiTag sx={{ ml: 0.75, verticalAlign: "middle" }}>AI generated</AiTag>}
          </Box>
        ) : null}

        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.75, mt: 1.25 }}>
          <Box
            component="span"
            sx={{ ...TYPE.itemName, color: C.ink, textDecoration: soldOut ? "line-through" : "none", fontVariantNumeric: "tabular-nums" }}
          >
            {priceLabel(food, currencySymbol)}
            {servesLabel(food) && (
              <Box component="span" sx={{ fontWeight: 500, color: C.muted, whiteSpace: "nowrap" }}>
                {` · ${servesLabel(food)}`}
              </Box>
            )}
          </Box>
          {cateringTarget && (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenCatering(cateringTarget);
              }}
              sx={{
                ...BUTTON_RESET,
                position: "relative",
                zIndex: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                height: 28,
                px: 1.25,
                borderRadius: RADIUS.pill,
                border: `1px solid ${C.borderStrong}`,
                bgcolor: C.redSoft,
                color: C.redLink,
                fontFamily: FONT_BODY,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: "nowrap",
                "&::before": { content: '""', position: "absolute", inset: "-8px 0" },
                "&:hover": { borderColor: C.red },
                ...FOCUS_RING,
              }}
            >
              <RoomServiceRoundedIcon sx={{ fontSize: 15 }} />
              Available for catering
            </Box>
          )}
          {!hasPhoto && control && <Box sx={{ ml: "auto", position: "relative", zIndex: 1 }}>{control}</Box>}
        </Box>
      </Box>

      {hasPhoto && (
        <Box sx={{ position: "relative", flexShrink: 0, width: 120, height: 120, alignSelf: "flex-start" }}>
          {/* An <img> rather than a CSS background: a background downloads as
              soon as the card renders, however far down the menu it is. */}
          <Box
            component="img"
            src={resizedImage(food.image, 240)}
            alt={food.title}
            loading="lazy"
            decoding="async"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              borderRadius: RADIUS.controlLg,
              bgcolor: C.surface,
              objectFit: "cover",
              objectPosition: "center",
              filter: soldOut ? "grayscale(100%)" : "none",
            }}
          />
          {food.isAiImage && <AiTag onPhoto sx={{ position: "absolute", top: 6, left: 6, pointerEvents: "none" }}>AI image</AiTag>}
          {control && (
            <Box sx={{ position: "absolute", right: -8, bottom: -8, zIndex: 1 }}>{control}</Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default React.memo(MenuItemCard);
